import type { ConcreteRequest } from 'relay-runtime';

import { PlatformSiteAri } from '@atlassian/ari';

import {
	getThirdPartyRecentVars,
	WORKED_ON_WITH_3P_EVENT_TYPES,
} from '@confluence/side-navigation/entry-points/recentThirdPartyItems.ts';

import { ActivitiesObjectType, ActivityEventType } from '../homeViewsQueryHelpers.ts';

import HomeRecentActivityQuery from './__generated__/HomeRecentActivityQuery.graphql.ts';
import HomeRecentCreatedQuery from './__generated__/HomeRecentCreatedQuery.graphql.ts';

const WORKED_ON_EVENT_TYPES_V3 = [
	ActivityEventType.EDITED,
	ActivityEventType.PUBLISHED,
	ActivityEventType.UPDATED,
	ActivityEventType.CREATED,
	ActivityEventType.STARTED,
	ActivityEventType.SNAPSHOTTED,
];

const COLLABORATED_ON_EVENT_TYPES = [ActivityEventType.COMMENTED, ...WORKED_ON_EVENT_TYPES_V3];
const COLLABORATED_ON_WITH_3P_EVENT_TYPES = ['commented', ...WORKED_ON_WITH_3P_EVENT_TYPES];

const OBJECT_TYPES = [
	ActivitiesObjectType.PAGE,
	ActivitiesObjectType.BLOGPOST,
	ActivitiesObjectType.EMBED,
];

const PAGE_SIZE = 24;

export const getRecentQueryOptions = ({
	pill,
	cloudId,
	endCursor,
	isWhiteboardViewedEnabled,
	isWhiteboardUpdatedEnabled,
	isDatabaseEnabled,
	include3P,
}: {
	pill: string | undefined;
	cloudId: string;
	endCursor: string;
	isWhiteboardViewedEnabled?: boolean;
	isWhiteboardUpdatedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	/** Adds third-party (3P) items to the activity pills. The page and the preloader read the experiment themselves. */
	include3P?: boolean;
}):
	| {
			query: ConcreteRequest;
			variables: {
				cql: string;
				cqlcontext: string;
				first: number;
				after: string;
				cloudId?: undefined;
				eventTypes?: undefined;
				objectTypes?: undefined;
			};
	  }
	| {
			query: ConcreteRequest;
			variables: {
				cloudId: string;
				first: number;
				after: string;
				products?: string[];
				eventTypes: string[];
				objectTypes: string[];
				cql?: undefined;
				cqlcontext?: undefined;
			};
	  } => {
	const createdDateFilter = 'and created >= startOfDay("-90d") ';

	const activityQuery = HomeRecentActivityQuery;
	const activityCloudId = PlatformSiteAri.create({ siteId: cloudId }).toString();

	switch (pill) {
		case 'created':
			return {
				query: HomeRecentCreatedQuery,
				variables: {
					cql: `type in (page, blogpost, embed${
						isWhiteboardUpdatedEnabled ? ', whiteboard' : ''
					}${isDatabaseEnabled ? ', database' : ''}) and owner=currentUser() ${createdDateFilter}order by created desc`,
					cqlcontext: `{"contentStatuses":["draft", "current"]}`,
					first: PAGE_SIZE,
					after: endCursor,
				},
			};
		case 'worked-on': {
			const objectTypes = [
				...OBJECT_TYPES,
				ActivitiesObjectType.CONTENT,
				...(isWhiteboardUpdatedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
				...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
			];
			return {
				query: activityQuery,
				variables: {
					cloudId: activityCloudId,
					first: PAGE_SIZE,
					after: endCursor,
					...(include3P
						? getThirdPartyRecentVars(objectTypes, WORKED_ON_WITH_3P_EVENT_TYPES)
						: { eventTypes: WORKED_ON_EVENT_TYPES_V3, objectTypes }),
				},
			};
		}
		case 'collaborated-on': {
			const objectTypes = [
				...OBJECT_TYPES,
				...(isWhiteboardUpdatedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
				...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
				ActivitiesObjectType.CONTENT,
				ActivitiesObjectType.COMMENT,
			];
			return {
				query: activityQuery,
				variables: {
					cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
					first: PAGE_SIZE,
					after: endCursor,
					...(include3P
						? getThirdPartyRecentVars(objectTypes, COLLABORATED_ON_WITH_3P_EVENT_TYPES)
						: { eventTypes: COLLABORATED_ON_EVENT_TYPES, objectTypes }),
				},
			};
		}
		case 'all':
		default: {
			const objectTypes = [
				...OBJECT_TYPES,
				ActivitiesObjectType.CONTENT,
				...(isWhiteboardViewedEnabled ? [ActivitiesObjectType.WHITEBOARD] : []),
				...(isDatabaseEnabled ? [ActivitiesObjectType.DATABASE] : []),
			];
			return {
				query: activityQuery,
				variables: {
					cloudId: activityCloudId,
					first: PAGE_SIZE,
					after: endCursor,
					...(include3P ? getThirdPartyRecentVars(objectTypes) : { eventTypes: [], objectTypes }),
				},
			};
		}
	}
};
